import React, { useState } from 'react';
import {
  H1,
  H2,
  Body,
  Button,
  Card,
  Badge,
  Modal,
  ConfirmDialog,
  useToast,
  FileUpload,
} from '../components/ui';

const UIDemo: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState<'danger' | 'warning' | 'info' | 'success'>('info');
  const { addToast } = useToast();

  const handleFileUpload = (files: File[]) => {
    console.log('Files uploaded:', files);
    addToast({
      title: 'Files Uploaded',
      message: `${files.length} file(s) uploaded successfully`,
      type: 'success',
    });
  };

  const showToast = (type: 'success' | 'error' | 'warning' | 'info') => {
    addToast({
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} Toast`,
      message: `This is a ${type} toast message`,
      type,
    });
  };

  const handleConfirm = () => {
    addToast({
      title: 'Confirmed',
      message: 'Action was confirmed',
      type: 'success',
    });
    setShowConfirmDialog(false);
  };

  const modalActions = [
    {
      label: 'Cancel',
      onClick: () => setShowModal(false),
      variant: 'secondary' as const,
    },
    {
      label: 'Save',
      onClick: () => {
        addToast({
          title: 'Saved',
          message: 'Modal content saved successfully',
          type: 'success',
        });
        setShowModal(false);
      },
      variant: 'primary' as const,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="mb-8">
        <H1 className="mb-2">UI Components Demo</H1>
        <Body className="text-gray-600">
          Showcase of all available UI components with interactive examples.
        </Body>
      </div>

      {/* Modal Demo */}
      <Card className="p-6">
        <H2 className="mb-4">Modal System</H2>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setShowModal(true)}>
              Open Modal
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmVariant('danger');
                setShowConfirmDialog(true);
              }}
            >
              Danger Confirm
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmVariant('warning');
                setShowConfirmDialog(true);
              }}
            >
              Warning Confirm
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmVariant('info');
                setShowConfirmDialog(true);
              }}
            >
              Info Confirm
            </Button>
          </div>

          <Modal
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            title="Example Modal"
            actions={modalActions}
          >
            <div className="space-y-4">
              <Body>
                This is an example modal with a title, content, and action buttons.
                You can customize the size, actions, and behavior.
              </Body>
              <div className="p-4 bg-gray-50 rounded-lg">
                <Body className="text-sm">
                  Modal content can include any React components, forms, or other UI elements.
                </Body>
              </div>
            </div>
          </Modal>

          <ConfirmDialog
            isOpen={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
            onConfirm={handleConfirm}
            title="Confirm Action"
            message="Are you sure you want to proceed with this action? This action cannot be undone."
            variant={confirmVariant}
            confirmText="Yes, proceed"
            cancelText="Cancel"
          />
        </div>
      </Card>

      {/* Toast Demo */}
      <Card className="p-6">
        <H2 className="mb-4">Toast Notifications</H2>
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => showToast('success')}>
            Success Toast
          </Button>
          <Button onClick={() => showToast('error')}>
            Error Toast
          </Button>
          <Button onClick={() => showToast('warning')}>
            Warning Toast
          </Button>
          <Button onClick={() => showToast('info')}>
            Info Toast
          </Button>
        </div>
      </Card>

      {/* File Upload Demo */}
      <Card className="p-6">
        <H2 className="mb-4">File Upload</H2>
        <div className="space-y-6">
          <div>
            <H2 className="text-lg mb-3">Single File Upload</H2>
            <FileUpload
              onFilesChange={handleFileUpload}
              accept="image/*"
              maxSize={5 * 1024 * 1024} // 5MB
              dragDropText="Drag and drop an image here"
              browseText="Browse images"
            />
          </div>

          <div>
            <H2 className="text-lg mb-3">Multiple File Upload</H2>
            <FileUpload
              onFilesChange={handleFileUpload}
              multiple
              maxFiles={3}
              accept=".pdf,.doc,.docx"
              dragDropText="Drag and drop documents here"
              browseText="Browse documents"
            />
          </div>
        </div>
      </Card>

      {/* Badges Demo */}
      <Card className="p-6">
        <H2 className="mb-4">Badges</H2>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Badge variant="default">Default</Badge>
            <Badge variant="primary">Primary</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="danger">Danger</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="info">Info</Badge>
          </div>
          <div className="flex flex-wrap gap-3">
            <Badge variant="primary" size="sm">Small</Badge>
            <Badge variant="primary" size="md">Medium</Badge>
            <Badge variant="primary" size="lg">Large</Badge>
          </div>
        </div>
      </Card>

      {/* Buttons Demo */}
      <Card className="p-6">
        <H2 className="mb-4">Buttons</H2>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button isLoading>Loading</Button>
            <Button disabled>Disabled</Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UIDemo;
